import { useState } from 'react';

import { AutocompleteInput } from '@/components';
import type { Option } from '@/components/inputs/AutocompleteInput';

import { COMMERCE_STATUS_LABELS, type Commerce } from '../../../models';
import { useCommercesOptions } from '../hooks';

interface CommerceSelectPanelProps {
  selected: Commerce | null;
  onSelect: (commerce: Commerce | null) => void;
  disabled?: boolean;
}

const getCommerce = (option?: Option | null): Commerce | null =>
  (option?.data as Commerce | undefined) ?? null;

const CommerceSelectPanel = ({
  selected,
  onSelect,
  disabled = false,
}: CommerceSelectPanelProps) => {
  const [search, setSearch] = useState('');
  const { commerceOptions, isLoadingCommerces } = useCommercesOptions({
    search,
  });

  const selectedOption: Option | null = selected
    ? { label: selected.name, data: selected }
    : null;

  return (
    <div className='w-full p-5 bg-white rounded-lg shadow-md flex flex-col gap-3'>
      <h3 className='text-gray-800 font-semibold'>Buscar comercio</h3>

      <AutocompleteInput
        name='commerceSearch'
        placeholder={
          isLoadingCommerces ? 'Buscando...' : 'Buscar comercio por nombre'
        }
        value={selectedOption}
        options={commerceOptions}
        disabled={disabled}
        loading={isLoadingCommerces}
        onInputChange={setSearch}
        onChange={(option) => onSelect(getCommerce(option))}
      />

      <p className='text-gray-500 text-sm'>
        Escribí al menos 3 letras del nombre del comercio.
      </p>

      {selected && (
        <div className='grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm text-gray-700 border-t border-gray-100 pt-3'>
          <strong>Nombre:</strong>
          <span>{selected.name}</span>

          <strong>Razón social:</strong>
          <span>{selected.businessName}</span>

          <strong>ID externo:</strong>
          <span>{selected.externalId || '-'}</span>

          <strong>Estado:</strong>
          <span>
            {COMMERCE_STATUS_LABELS[
              selected.status as keyof typeof COMMERCE_STATUS_LABELS
            ] ?? selected.status}
          </span>
        </div>
      )}
    </div>
  );
};

export default CommerceSelectPanel;
